import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useOutletContext, useLocation } from "react-router-dom";
import PatientCIForm from "../Forms/PatientCIForm";
import { usePatient } from "../../context/PatientContext";
import { useAuth } from "../../context/AuthProvider";
import { useTheme } from "../../context/ThemeContext";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";

const EditPatientCI = () => {
  const location = useLocation();
  const { patientData, setPatientData, selectedCase, selectedVisit, doctorInfo } = usePatient();
  const { theme } = useTheme();
  const { patientId, caseId, visitId } = useParams();
  const navigate = useNavigate();
  const [loading] = useState(false);
  const { markSectionComplete } = useOutletContext();
  const { verifyTokenAndRedirect } = useAuth();

  useEffect(() => {
    verifyTokenAndRedirect();
  }, [verifyTokenAndRedirect]);

  const onSubmitHandler = (updatedPatientCI, options = {}) => {
    const isAutoSave = options?.isAutoSave;
    const updatedData = {
      ...patientData,
      patientClinicalInvestigation: patientData?.patientClinicalInvestigation?.length > 0
        ? patientData.patientClinicalInvestigation.map(info => ({
          ...info,
          ...updatedPatientCI,
          PATIENT_ID: patientData.PATIENT_ID,
          CASE_ID: selectedCase || caseId,
          VISIT_ID: selectedVisit || visitId,
          DOCTOR_ID: doctorInfo.MANDALI_ID,
          CLINIC_ID: doctorInfo.CLINIC_ID
        }))
        : [{
          ...updatedPatientCI,
          PATIENT_ID: patientData.PATIENT_ID,
          CASE_ID: selectedCase || caseId,
          VISIT_ID: selectedVisit || visitId,
          DOCTOR_ID: doctorInfo.MANDALI_ID,
          CLINIC_ID: doctorInfo.CLINIC_ID
        }]
    };

    setPatientData(updatedData);
    markSectionComplete("ci");
    if (!isAutoSave) {
      if (location.pathname.startsWith("/addpatient")) {
        navigate("/addpatient/anyavyadhi");
      } else if (location.pathname.startsWith("/visits")) {
        navigate(`/visits/${patientId}/${caseId}/${visitId}/anyavyadhi`);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <Loader2 className="animate-spin mb-4" size={40} style={{ color: theme.primary }} />
        <p className="font-medium opacity-60" style={{ color: theme.text }}>Loading Clinical Investigation...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-10">
        <h3
          className="text-2xl md:text-4xl mb-3 tracking-tight"
          style={{
            fontFamily: theme.fonts?.serif,
            color: theme.text
          }}
        >
          Clinical Investigation
        </h3>
        <div className="h-1 w-12 rounded-full mb-6" style={{ backgroundColor: theme.primary }} />
        <p className="text-sm md:text-base opacity-60 font-medium" style={{ color: theme.textSecondary }}>
          Recording detailed physical examinations, vital signs, and observed clinical findings.
        </p>
      </div>

      {patientData || (patientId === undefined && caseId === undefined && visitId === undefined) ? (
        <PatientCIForm
          initialValues={patientData}
          onSubmit={onSubmitHandler}
          onAutoSave={(values) => onSubmitHandler(values, { isAutoSave: true })}
          enableReinitialize={true}
        />
      ) : (
        <div className="flex flex-col items-center justify-center py-20">
          <Loader2 className="animate-spin mb-4" size={32} style={{ color: theme.primary }} />
          <p className="opacity-60">Initializing clinical records...</p>
        </div>
      )}
    </div>
  );

};

export default EditPatientCI;


